import { useId } from "react"

import {
  Button,
  Card,
  CardContent,
  FormField,
  Input,
  SettingRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@coval/ui"
import { Plus, X } from "lucide-react"

interface CompositeCriteriaFieldsProps {
  criteriaSource: string
  criteriaPath: string
  criteria: string[]
  onSourceChange: (source: "metric_metadata" | "test_case_attribute") => void
  onPathChange: (path: string) => void
  onCriterionChange: (index: number, criterion: string) => void
  onAddCriterion: () => void
  onRemoveCriterion: (index: number) => void
}

export function CompositeCriteriaFields({
  criteriaSource,
  criteriaPath,
  criteria,
  onSourceChange,
  onPathChange,
  onCriterionChange,
  onAddCriterion,
  onRemoveCriterion,
}: CompositeCriteriaFieldsProps) {
  const descriptionId = useId()
  return (
    <SettingRow
      stacked
      label="Criteria Location"
      tooltip="Criteria are the requirements the agent is judged against."
    >
      {(id) => (
        <Tabs
          value={
            criteriaSource === "test_case"
              ? "test_case_attribute"
              : criteriaSource
          }
          onValueChange={(value) => {
            if (
              value === "metric_metadata" ||
              value === "test_case_attribute"
            ) {
              onSourceChange(value)
            }
          }}
        >
          <TabsList
            id={id}
            variant="pill"
            aria-label="Criteria Location"
            aria-describedby={descriptionId}
            className="w-full"
          >
            <TabsTrigger
              variant="pill"
              value="metric_metadata"
              className="flex-1"
            >
              Global
            </TabsTrigger>
            <TabsTrigger
              variant="pill"
              value="test_case_attribute"
              className="flex-1"
            >
              Test Case
            </TabsTrigger>
          </TabsList>
          <p id={descriptionId} className="mt-2 text-xs text-coval-text-muted">
            {criteriaSource === "metric_metadata"
              ? "The same criteria apply to every test case evaluated by this metric."
              : "Criteria are read from each test case’s attributes and can differ between test cases."}
          </p>
          <TabsContent value="metric_metadata" className="space-y-3">
            <FormField
              label="Criteria"
              tooltip="Write the criteria this metric will evaluate in every conversation."
            >
              {(ids) => (
                <div {...ids} role="group" className="space-y-2">
                  {criteria.map((criterion, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={criterion}
                        onChange={(event) =>
                          onCriterionChange(index, event.target.value)
                        }
                        placeholder={`Criterion ${index + 1}`}
                        aria-label={`Criterion ${index + 1}`}
                      />
                      {criteria.length > 1 && (
                        <Button
                          icon
                          variant="ghost"
                          onClick={() => onRemoveCriterion(index)}
                          tooltip={null}
                          aria-label={`Remove criterion ${index + 1}`}
                        >
                          <X className="size-4 text-coval-danger-text" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="secondary" onClick={onAddCriterion}>
                    <Plus className="size-4" />
                    Add Criterion
                  </Button>
                </div>
              )}
            </FormField>
          </TabsContent>
          <TabsContent value="test_case_attribute" className="space-y-3">
            <FormField
              label="Criteria Attribute"
              description="Enter the existing test case attribute that contains the criteria. This metric reads the attribute; it does not create or edit test case data."
              tooltip="Attributes are stored in the test case’s expected output JSON. Use dots for nested attributes, such as review.evaluation_criteria. The attribute must contain a list of criteria written as strings."
            >
              {(ids) => (
                <Input
                  {...ids}
                  value={criteriaPath}
                  onChange={(event) => onPathChange(event.target.value)}
                  placeholder="e.g., evaluation_criteria"
                />
              )}
            </FormField>
            <Card>
              <CardContent className="space-y-2 p-3">
                <p className="text-xs font-medium text-coval-text">
                  Example test case attributes
                </p>
                <pre className="whitespace-pre-wrap break-words text-xs text-coval-text-muted">{`{
  "evaluation_criteria": [
    "The agent explains the next step.",
    "The agent confirms the customer's request."
  ]
}`}</pre>
                <p className="text-xs text-coval-text-muted">
                  Enter <code>evaluation_criteria</code> above to read this list
                  of criteria from that test case attribute.
                </p>
              </CardContent>
            </Card>
            <p className="text-xs text-coval-warning-text">
              This attribute must already contain a list of criteria on every
              test case evaluated by this metric.
            </p>
          </TabsContent>
        </Tabs>
      )}
    </SettingRow>
  )
}
